import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Settings, 
  Calculator, 
  Factory, 
  ShoppingCart, 
  DollarSign, 
  Receipt, 
  BarChart3,
  Package,
  Users,
  Boxes,
  UserCog,
  UsersRound,
  Shield,
  Menu,
  X,
  Ban,
  Mountain,
  Layers
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const Sidebar = () => {
  const { user, hasPermission } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Define menu items with permission requirements
  const allMenuItems = [
    { icon: LayoutDashboard, label: 'Tableau de bord', path: '/', permission: null },
    { icon: Settings, label: 'Suivi MP', path: '/configuration', permission: 'can_manage_config' },
    { icon: Calculator, label: 'Calculateur', path: '/calculator', permission: null },
    { icon: Factory, label: 'Production', path: '/production', permission: 'can_manage_production' },
    { icon: Users, label: 'Clients', path: '/clients', permission: 'can_manage_clients' },
    { icon: Boxes, label: 'Produits & Services', path: '/products-services', permission: 'can_manage_products' },
    { icon: ShoppingCart, label: 'Ventes', path: '/sales', permission: 'can_manage_sales' },
    { icon: Layers, label: 'Ventes Agglos', path: '/agglo-sales', permission: 'can_manage_sales' },
    { icon: Mountain, label: 'Granulats', path: '/granulats', permission: 'can_manage_sales' },
    { icon: Receipt, label: 'Achats', path: '/purchases', permission: 'can_manage_purchases' },
    { icon: DollarSign, label: 'Dépenses', path: '/expenses', permission: 'can_manage_expenses' },
    { icon: UserCog, label: 'RH / Salaires', path: '/rh', permission: 'can_manage_hr' },
    { icon: Package, label: 'Stocks', path: '/stock', permission: 'can_manage_stock' },
    { icon: Ban, label: 'Rebus', path: '/rebus', permission: 'can_manage_production' },
    { icon: BarChart3, label: 'Analyses', path: '/analytics', permission: 'can_view_analytics' },
    { icon: UsersRound, label: 'Utilisateurs', path: '/users', permission: 'can_manage_users' },
  ];

  // Filter menu items based on permissions
  const menuItems = allMenuItems.filter(item => {
    if (!item.permission) return true;
    return hasPermission(item.permission);
  });

  const getRoleLabel = (role) => {
    const labels = {
      admin: 'Administrateur',
      operateur: 'Opérateur',
      commercial: 'Commercial',
      comptable: 'Comptable'
    };
    return labels[role] || role;
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <>
      {/* Mobile Menu Button - Fixed at top left */}
      <button
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-[#1B3268] text-white rounded-lg shadow-lg"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        data-testid="mobile-menu-button"
      >
        {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={closeMobileMenu}
        />
      )}

      {/* Mobile Menu - Full Screen */}
      <div className={`lg:hidden fixed inset-0 z-40 bg-[#1B3268] transform transition-transform duration-300 ${
        mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex flex-col h-full pt-16 pb-4">
          {/* Logo */}
          <div className="p-4 border-b border-white/10">
            <div className="flex items-center justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_granulat-control/artifacts/k87nilu3_10.png" 
                alt="AGRABAT" 
                className="h-12 object-contain brightness-0 invert"
              />
            </div>
          </div>

          {/* Navigation Grid for Mobile */}
          <nav className="flex-1 p-4 overflow-y-auto">
            <div className="grid grid-cols-2 gap-3">
              {menuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    end={item.path === '/'}
                    onClick={closeMobileMenu}
                    data-testid={`mobile-nav-${item.path.replace('/', '') || 'dashboard'}`}
                    className={({ isActive }) =>
                      `flex flex-col items-center justify-center gap-2 p-4 rounded-lg transition-all ${
                        isActive
                          ? 'bg-[#FDC800] text-[#1B3268] font-medium'
                          : 'bg-white/10 text-white hover:bg-white/20'
                      }`
                    }
                  >
                    <Icon className="w-6 h-6" />
                    <span className="text-xs text-center">{item.label}</span>
                  </NavLink>
                );
              })}
            </div>
          </nav>

          {/* User Info */}
          <div className="p-4 border-t border-white/10">
            <div className="flex items-center gap-2 justify-center">
              <div className="w-8 h-8 rounded-full bg-[#FDC800] flex items-center justify-center text-[#1B3268] font-bold text-sm">
                {user?.full_name?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-white truncate">{user?.full_name}</div>
                <div className="flex items-center gap-1 text-xs text-[#FDC800]">
                  <Shield className="w-3 h-3" />
                  {getRoleLabel(user?.role)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Sidebar - Hidden on mobile */}
      <aside className="hidden lg:flex w-64 bg-[#1B3268] text-white flex-col shadow-xl" data-testid="sidebar">
        {/* Logo */}
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center justify-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_granulat-control/artifacts/k87nilu3_10.png" 
              alt="AGRABAT" 
              className="h-12 object-contain brightness-0 invert"
            />
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-6 px-3 space-y-1 overflow-y-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/'}
                data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-sm transition-all ${
                    isActive
                      ? 'bg-white/10 border-l-4 border-[#FDC800] text-white font-medium'
                      : 'hover:bg-white/5 text-white/80 hover:text-white border-l-4 border-transparent'
                  }`
                }
              >
                <Icon className="w-5 h-5" />
                <span className="text-sm">{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        {/* User Info */}
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#FDC800] flex items-center justify-center text-[#1B3268] font-bold text-sm">
              {user?.full_name?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user?.full_name}</div>
              <div className="flex items-center gap-1 text-xs text-[#FDC800]">
                <Shield className="w-3 h-3" />
                {getRoleLabel(user?.role)}
              </div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
